/**
 * MoneyAmount — the single source of truth for rendering monetary values.
 *
 * Guarantees a production-grade, zero-CLS fintech appearance:
 *  • Currency symbol, sign, and digits ALWAYS stay on one line (whitespace-nowrap).
 *  • Tabular numerals so digits align across rows (font-variant-numeric: tabular-nums).
 *  • Baseline-aligned inline-flex so the sign never wraps to a new line.
 *
 * Coloring is opt-in and semantic, driven by `tone`:
 *   - "auto"     (default): neutral, but shows RED when value < 0.
 *                Use for balances / totals / budgets — where a negative is the only
 *                signal worth colouring.
 *   - "neutral":  never coloured. Just formatting.
 *   - "income"    | "positive": always green.
 *   - "expense"   | "negative": always red.
 *   - "signed":   green when ≥ 0, red when < 0 (use for deltas / gains / P&L).
 *
 * `signed` prop forces the display of an explicit `+` for non-negative values
 * and `−` (U+2212) for negatives. Negatives are always shown with `−`
 * regardless of `signed` — we strip the platform's default sign from
 * Intl.NumberFormat by formatting the absolute value and prepending our own,
 * so it can never break onto a separate line.
 *
 * Alternatively pass `preformatted` when the amount has already been formatted
 * (e.g. via `useDisplayMoney().fmt()` which applies FX conversion). In that
 * case we still enforce nowrap + tabular-nums.
 */
import { useAuth } from "@/context/AuthContext";
import { formatMoney as baseFormatMoney } from "@/lib/currency";
import { cn } from "@/lib/utils";

const TONE_CLASS = {
  neutral:  "",
  income:   "text-emerald-700 dark:text-emerald-400",
  positive: "text-emerald-700 dark:text-emerald-400",
  expense:  "text-[#B91C1C] dark:text-[#FCA5A5]",
  negative: "text-[#B91C1C] dark:text-[#FCA5A5]",
};

function resolveTone(tone, isNegative) {
  if (tone === "auto") return isNegative ? "negative" : "neutral";
  if (tone === "signed") return isNegative ? "negative" : "positive";
  return tone || "neutral";
}

export function MoneyAmount({
  value,
  preformatted,
  currency,
  compact = false,
  tone = "auto",
  signed = false,
  className,
  as: Comp = "span",
  ...rest
}) {
  const { user } = useAuth();
  const displayCurrency = currency || user?.currency || "USD";

  let prefix = "";
  let body = "";
  let isNegative = false;

  if (preformatted != null) {
    // Already-formatted string (e.g. from useDisplayMoney().fmt(...))
    const s = String(preformatted);
    // Strip a leading hyphen-minus or Unicode minus, if any, so we control
    // the sign character and prevent line-break between "-" and digits.
    if (s.startsWith("-") || s.startsWith("\u2212")) {
      isNegative = true;
      body = s.slice(1);
      prefix = "\u2212";
    } else {
      body = s;
      if (signed && Number.isFinite(Number(value)) && Number(value) > 0) prefix = "+";
    }
  } else {
    const num = Number(value) || 0;
    isNegative = num < 0;
    const abs = Math.abs(num);
    body = baseFormatMoney(abs, displayCurrency, { compact });
    if (isNegative) prefix = "\u2212"; // U+2212 typographic minus
    else if (signed && num > 0) prefix = "+";
  }

  const effectiveTone = resolveTone(tone, isNegative);
  const colorClass = TONE_CLASS[effectiveTone] || "";

  return (
    <Comp
      className={cn(
        "inline-flex items-baseline whitespace-nowrap [font-variant-numeric:tabular-nums]",
        colorClass,
        className,
      )}
      {...rest}
    >
      {prefix}
      {body}
    </Comp>
  );
}

export default MoneyAmount;
